import React, { useMemo, useState } from 'react';
import { Button } from '@crisp/crisp-ui/cabbage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy } from '@crisp/crisp-ui/icons/classic/regular';
import hljs from 'highlight.js/lib/core';
import { SYNTAX_LANGUAGES } from '../../lib/syntaxLanguages';
import styles from './CodeBlock.module.css';

for (const [name, grammar] of Object.entries(SYNTAX_LANGUAGES)) {
  hljs.registerLanguage(name, grammar);
}

/** Fence languages that say nothing a reader can't see for themselves. */
const UNLABELLED_LANGUAGES = new Set(['', 'text', 'txt', 'plaintext', 'plain', 'code']);

export interface CodeBlockProps {
  children?: React.ReactNode;
  className?: string;
  language?: string;
}

const getTextContent = (node: React.ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (!node) return '';
  if (Array.isArray(node)) return node.map(getTextContent).join('');
  if (React.isValidElement(node)) {
    return getTextContent((node.props as { children?: React.ReactNode })?.children);
  }
  return '';
};

export const CodeBlock: React.FC<CodeBlockProps> = ({
  children,
  className = '',
  language: explicitLanguage,
}) => {
  const [copied, setCopied] = useState(false);

  const codeText = getTextContent(children).replace(/\n$/, '');

  let lang = explicitLanguage || '';
  if (!lang && className) {
    const classStr = Array.isArray(className) ? className.join(' ') : String(className);
    const match = classStr.match(/language-([a-zA-Z0-9_-]+)/);
    if (match) {
      lang = match[1];
    }
  }
  lang = lang.toLowerCase();

  // hljs escapes the source before wrapping tokens in spans, so the result is
  // safe to inject.
  const highlighted = useMemo(
    () => (lang && hljs.getLanguage(lang) ? hljs.highlight(codeText, { language: lang }).value : null),
    [codeText, lang],
  );

  const handleCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(codeText);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = codeText;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code: ', err);
    }
  };

  return (
    <div className={styles.root}>
      <pre className={styles.pre}>
        {highlighted !== null ? (
          <code className={styles.code} dangerouslySetInnerHTML={{ __html: highlighted }} />
        ) : (
          <code className={styles.code}>{children}</code>
        )}
      </pre>
      <div className={styles.actions}>
        {!UNLABELLED_LANGUAGES.has(lang) && <span className={styles.lang}>{lang}</span>}
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className={styles.copy}
          aria-label={copied ? 'Copied' : 'Copy code'}
          data-copied={copied || undefined}
          onClick={handleCopy}
          iconPrimary={<FontAwesomeIcon icon={copied ? faCheck : faCopy} />}
        />
      </div>
    </div>
  );
};

export default CodeBlock;
